import { useState } from 'react'
import { supabase } from '../api/supabase'
import { explainSageError } from '../lib/sageErrorMessage'

interface SageSyncResult {
  success: boolean
  sageContactId?: string
  error?: string
}

interface SageInvoiceSyncResult {
  success: boolean
  sageInvoiceId?: string
  alreadySynced?: boolean
  error?: string
}

// Z65 addenda: BoxTop's own invoice CSV export columns -- fixed/known headers (not a variable
// mapping-UI situation), see Admin/SageBulkImport.tsx for the parser.
export interface SageCsvRow {
  Type: string
  'Customer Reference': string
  Date: string
  'Customer Name': string
  Reference: string
  'Ledger Account': string
  Details: string
  Net: string | number
  'VAT Rate': string
  VAT: string | number
  Total: string | number
  // Z68: optional -- not a native BoxTop export column yet. Empty means "don't attach a
  // Department", not a resolution failure.
  Department?: string
  // Z67.19: optional -- overrides the fixed "FREIGHT FORWARDING SALES" line description with a
  // specific service type for this row.
  "Service Type"?: string
  // Z67.22.2: optional -- third free-text segment appended to the Sage reference (see
  // buildSageReference below). Not a native BoxTop export column; Gary adds it by hand.
  // Z67.23: column renamed "Additional Reference" -> "CustomerRef" -- Sage updated its own
  // Bulk Import CSV format to use this header. Same optional field, just a new name.
  CustomerRef?: string
}

// Z69: mirrors buildSageReference in supabase/functions/_shared/sage.ts -- the actual combining
// happens server-side when syncing, this copy is only so the Bulk Import Preview table can show
// Gary exactly what will be sent before he hits Sync.
//
// Z67.22.2: generalized to an optional third "CustomerRef" segment (renamed from "Additional
// Reference" in Z67.23), e.g. "K10503/KI24365/AAA" -- same graceful degradation, any missing
// part is just skipped.
export function buildSageReference(reference: string, details: string, additional?: string): string | undefined {
  const parts = [reference, details, additional].map(p => (p || '').trim()).filter(Boolean)
  return parts.length > 0 ? parts.join('/') : undefined
}

export interface SageDepartmentCategory {
  code: string
  id: string
  name: string
}

// Z75: for the Customer edit form's default-ledger/tax-rate dropdowns.
export interface SageLedgerAccount {
  code: string | null
  id: string
  name: string
}
export interface SageTaxRate {
  id: string
  name: string
}

export interface SageCsvRowResult {
  row_index: number
  success: boolean
  error?: string
  sageInvoiceId?: string
  // Z73.22: only set for a failure that actually reached Sage's API -- a row that failed our own
  // pre-flight validation (unmapped ledger account, bad date, etc) never called Sage at all.
  httpStatus?: number
}

interface SageBulkImportResult {
  success: boolean
  batchId?: string
  results?: SageCsvRowResult[]
  error?: string
}

// Z73.22: fed back to the caller after every chunk so the UI can show "正在同步 第 2/3 批 ·
// 已完成 168/239 条" instead of running silently to completion. totalChunks is an ESTIMATE (the
// server decides its own real chunk size, currently BATCH_CHUNK_SIZE=80 in
// sage-sync-invoice/index.ts) purely for the progress label -- processedRows/totalRows are the
// real, authoritative numbers.
export interface SageBatchProgress {
  batchId: string
  totalRows: number
  processedRows: number
  chunkIndex: number
  totalChunks: number
}

export interface SageIncompleteBatch {
  id: string
  filename: string | null
  total_rows: number
  created_at: string
  pendingCount: number
  // Z73.24: first few still-pending rows (row # + Reference), for the Dismiss confirmation.
  pendingPreview: { row_index: number; reference: string }[]
  // Failed rows nobody has acknowledged yet and whose Reference never synced elsewhere.
  errorCount: number
  errorPreview: { row_index: number; reference: string; error_msg: string }[]
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))
// Z73.22: Gary's "批次之间加适当延迟避免触发 Sage 限流" -- a pause between chunk requests, not
// between individual rows within a chunk (those are already serialized server-side by awaiting
// each row's Sage call before starting the next).
const CHUNK_DELAY_MS = 750
// Mirrors the server's own default (BATCH_CHUNK_SIZE in sage-sync-invoice/index.ts) -- used only
// to estimate totalChunks for the progress label; the server enforces its own real chunk size
// regardless of this value.
const ESTIMATED_CHUNK_SIZE = 80

// Z65: same shape as useSyncToBoxtrax -- one hook, tracks in-flight ids so a per-row button
// can show its own loading state without a global spinner blocking the rest of the list.
export function useSyncToSage() {
  const [syncingContacts, setSyncingContacts] = useState<Set<string>>(new Set())
  const [syncingInvoices, setSyncingInvoices] = useState<Set<string>>(new Set())

  // Z73.23: didn't check response.ok before parsing JSON -- a platform-level failure (e.g. a
  // Cloudflare/Supabase gateway timeout returning an HTML error page, not this function's own
  // handled error response) used to surface as an opaque "Unexpected token '<' ... is not valid
  // JSON" instead of a message anyone could act on. Every caller already wraps this in try/catch,
  // so nothing crashed, but the error shown to Gary was useless. Now produces a clear message
  // either way.
  const callFunction = async <T,>(name: string, body: Record<string, unknown>): Promise<T> => {
    const { data: sessionData } = await supabase.auth.getSession()
    const accessToken = sessionData?.session?.access_token || ''
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${name}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify(body),
    })
    const text = await response.text()
    try {
      return JSON.parse(text)
    } catch {
      throw new Error(`${name} returned a non-JSON response (HTTP ${response.status} ${response.statusText}) -- likely a gateway/platform error, not this function's own response: ${text.slice(0, 200)}`)
    }
  }

  const syncContact = async (customerId: string): Promise<SageSyncResult> => {
    setSyncingContacts(prev => new Set([...prev, customerId]))
    try {
      return await callFunction<SageSyncResult>('sage-sync-contact', { customer_id: customerId })
    } catch (err: any) {
      return { success: false, error: err?.message || 'Failed to sync to Sage' }
    } finally {
      setSyncingContacts(prev => { const next = new Set(prev); next.delete(customerId); return next })
    }
  }

  const syncInvoice = async (invoiceId: string): Promise<SageInvoiceSyncResult> => {
    setSyncingInvoices(prev => new Set([...prev, invoiceId]))
    try {
      return await callFunction<SageInvoiceSyncResult>('sage-sync-invoice', { invoice_id: invoiceId })
    } catch (err: any) {
      return { success: false, error: err?.message || 'Failed to sync to Sage' }
    } finally {
      setSyncingInvoices(prev => { const next = new Set(prev); next.delete(invoiceId); return next })
    }
  }

  const isSyncingContact = (customerId: string) => syncingContacts.has(customerId)
  const isSyncingInvoice = (invoiceId: string) => syncingInvoices.has(invoiceId)

  // Z73.22: drives the actual sync as a loop of small server-side chunks (see
  // sage-sync-invoice/index.ts's continue_batch action) instead of one long request -- this IS
  // the fix for the 239-row batch that silently died at row 168 (Supabase killed the single
  // long-running request mid-flight; a chunk that dies the same way just leaves its rows
  // 'pending', and the NEXT chunk call picks them up, so this loop is naturally resumable even
  // across a dropped connection, not just a clean finish).
  const runBatchToCompletion = async (
    batchId: string,
    totalRows: number,
    alreadySynced: number,
    onProgress?: (progress: SageBatchProgress) => void
  ): Promise<SageBulkImportResult> => {
    const allResults: SageCsvRowResult[] = []
    let remainingPending = totalRows - alreadySynced
    let chunkIndex = 0
    const totalChunks = Math.max(1, Math.ceil(totalRows / ESTIMATED_CHUNK_SIZE))
    while (remainingPending > 0) {
      chunkIndex++
      onProgress?.({ batchId, totalRows, processedRows: totalRows - remainingPending, chunkIndex, totalChunks })
      const chunk = await callFunction<{ success: boolean; results?: SageCsvRowResult[]; remainingPending?: number; error?: string }>(
        'sage-sync-invoice', { action: 'continue_batch', batch_id: batchId }
      )
      if (!chunk.success) return { success: false, batchId, results: allResults, error: chunk.error || 'Batch chunk failed' }
      allResults.push(...(chunk.results || []))
      remainingPending = chunk.remainingPending ?? 0
      onProgress?.({ batchId, totalRows, processedRows: totalRows - remainingPending, chunkIndex, totalChunks })
      if (remainingPending > 0) await sleep(CHUNK_DELAY_MS)
    }
    return { success: true, batchId, results: allResults }
  }

  const bulkImportCsv = async (
    rows: SageCsvRow[],
    filename?: string,
    onProgress?: (progress: SageBatchProgress) => void
  ): Promise<SageBulkImportResult> => {
    try {
      const created = await callFunction<{ success: boolean; batchId?: string; totalRows?: number; error?: string }>(
        'sage-sync-invoice', { action: 'bulk_csv', rows, filename }
      )
      if (!created.success || !created.batchId) return { success: false, error: created.error || 'Failed to create import batch' }
      return await runBatchToCompletion(created.batchId, created.totalRows ?? rows.length, 0, onProgress)
    } catch (err: any) {
      return { success: false, error: err?.message || 'Failed to import' }
    }
  }

  // Z73.22: picks up an interrupted batch found via listIncompleteBatches -- no CSV re-upload
  // needed, continue_batch only ever looks at rows already persisted in sage_csv_import_rows.
  const resumeBatch = async (
    batch: SageIncompleteBatch,
    onProgress?: (progress: SageBatchProgress) => void
  ): Promise<SageBulkImportResult> => {
    try {
      return await runBatchToCompletion(batch.id, batch.total_rows, batch.total_rows - batch.pendingCount, onProgress)
    } catch (err: any) {
      return { success: false, error: err?.message || 'Failed to resume batch' }
    }
  }

  const listIncompleteBatches = async (): Promise<SageIncompleteBatch[]> => {
    try {
      const result = await callFunction<{ success: boolean; batches?: SageIncompleteBatch[]; error?: string }>(
        'sage-sync-invoice', { action: 'list_incomplete_batches' }
      )
      return result.success && Array.isArray(result.batches) ? result.batches : []
    } catch {
      return []
    }
  }

  const dismissErrors = async (
    batch: SageIncompleteBatch,
    reason?: string
  ): Promise<{ success: boolean; dismissedCount?: number; error?: string }> => {
    try {
      return await callFunction<{ success: boolean; dismissedCount?: number; error?: string }>(
        'sage-sync-invoice', { action: 'dismiss_errors', batch_id: batch.id, reason }
      )
    } catch (err: any) {
      return { success: false, error: err?.message || 'Failed to acknowledge failures' }
    }
  }

  // Z73.22: Gary's "Dismiss" ask -- give up on an interrupted batch's remaining pending rows
  // (handled outside the app) so it stops showing in the Resume panel. Server-side only ever
  // touches rows still 'pending' -- see dismissBatch's comment in sage-sync-invoice/index.ts.
  const dismissBatch = async (
    batch: SageIncompleteBatch,
    reason?: string
  ): Promise<{ success: boolean; dismissedCount?: number; error?: string }> => {
    try {
      return await callFunction<{ success: boolean; dismissedCount?: number; error?: string }>(
        'sage-sync-invoice', { action: 'dismiss_batch', batch_id: batch.id, reason }
      )
    } catch (err: any) {
      return { success: false, error: err?.message || 'Failed to dismiss batch' }
    }
  }

  // Z73.22: chunked the same way as the main sync loop (see retryBatch's server-side comment) --
  // a batch with more failures than fit in one invocation is exposed to the exact same timeout
  // this whole change exists to fix.
  const retryBatch = async (
    batchId: string,
    onProgress?: (progress: { processed: number }) => void
  ): Promise<SageBulkImportResult> => {
    try {
      const allResults: SageCsvRowResult[] = []
      let remainingError = 1
      while (remainingError > 0) {
        const chunk = await callFunction<{ success: boolean; results?: SageCsvRowResult[]; remainingError?: number; error?: string }>(
          'sage-sync-invoice', { action: 'retry_batch', batch_id: batchId }
        )
        if (!chunk.success) return { success: false, batchId, results: allResults, error: chunk.error || 'Retry chunk failed' }
        allResults.push(...(chunk.results || []))
        remainingError = chunk.remainingError ?? 0
        onProgress?.({ processed: allResults.length })
        if (remainingError > 0) await sleep(CHUNK_DELAY_MS)
      }
      return { success: true, batchId, results: allResults }
    } catch (err: any) {
      return { success: false, error: err?.message || 'Failed to retry' }
    }
  }

  // Z73.22: Gary's ask -- "失败清单可导出 CSV (行号 + 单号 + HTTP status + Sage error message)".
  // Pure client-side formatting/download, no new endpoint needed -- `results`/`rows` are already
  // in memory on the page that calls this.
  const exportFailuresCsv = (results: SageCsvRowResult[], rows: SageCsvRow[], filename?: string) => {
    const failures = results.filter(r => !r.success)
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`
    const lines = [
      ['Row', 'Reference', 'HTTP Status', 'Reason', 'Raw Sage error'].map(escape).join(','),
      ...failures.map(r => [
        String(r.row_index + 1),
        rows[r.row_index]?.Reference || rows[r.row_index]?.['Customer Name'] || '',
        r.httpStatus != null ? String(r.httpStatus) : '',
        explainSageError(r.error),
        r.error || '',
      ].map(escape).join(',')),
    ]
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${(filename || 'sage-sync').replace(/\.[^.]+$/, '')}-failures.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Z68: live list for the "Default Department for this upload" dropdown -- returns [] (not an
  // error) when Sage has no "Department" analysis type set up.
  const fetchDepartmentCategories = async (): Promise<SageDepartmentCategory[]> => {
    try {
      const result = await callFunction<{ success: boolean; items?: SageDepartmentCategory[]; error?: string }>(
        'sage-sync-invoice', { action: 'list_department_categories' }
      )
      return result.success && Array.isArray(result.items) ? result.items : []
    } catch {
      return []
    }
  }

  // Z75: live from Sage's own chart of accounts / tax rates -- same [] (not an error) on
  // failure convention as fetchDepartmentCategories above.
  const fetchLedgerAccounts = async (): Promise<SageLedgerAccount[]> => {
    try {
      const result = await callFunction<{ success: boolean; items?: SageLedgerAccount[]; error?: string }>(
        'sage-sync-invoice', { action: 'list_ledger_accounts' }
      )
      return result.success && Array.isArray(result.items) ? result.items : []
    } catch {
      return []
    }
  }

  const fetchTaxRates = async (): Promise<SageTaxRate[]> => {
    try {
      const result = await callFunction<{ success: boolean; items?: SageTaxRate[]; error?: string }>(
        'sage-sync-invoice', { action: 'list_tax_rates' }
      )
      return result.success && Array.isArray(result.items) ? result.items : []
    } catch {
      return []
    }
  }

  return {
    syncContact, syncInvoice, isSyncingContact, isSyncingInvoice,
    bulkImportCsv, retryBatch, resumeBatch, listIncompleteBatches, dismissBatch, dismissErrors, exportFailuresCsv,
    fetchDepartmentCategories, fetchLedgerAccounts, fetchTaxRates,
  }
}

// Relative-time label for a "synced X ago" / "never synced" per-row status -- no existing
// helper for this in the codebase (checked), kept small and local rather than pulling in a
// date library for one formatting need.
export function formatSyncStatus(lastSyncedAt: string | null | undefined): string {
  if (!lastSyncedAt) return 'Never synced'
  const diffMs = Date.now() - new Date(lastSyncedAt).getTime()
  const mins = Math.floor(diffMs / 60_000)
  if (mins < 1) return 'Synced just now'
  if (mins < 60) return `Synced ${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `Synced ${hours}h ago`
  const days = Math.floor(hours / 24)
  return `Synced ${days}d ago`
}
